import type { WeekWindow, ProposalView } from '@/types/current-project';
import type { ProposalDefinition } from '@/types/project-command';
import type { MilestonePresentation } from '@/api/adapter/proposalReviews';

export type PlanningMilestone = ProposalDefinition['milestones'][number];
export const definitionOf = (proposal: ProposalView): ProposalDefinition => ({
  title: proposal.title,
  description: proposal.description,
  milestones: proposal.milestones.map((milestone) => milestone.definition),
});
export function dateToWeek(value: string) {
  const date = new Date(value + 'T00:00:00Z');
  if (
    !Number.isFinite(date.getTime()) ||
    date.toISOString().slice(0, 10) !== value
  )
    throw new Error('Choose a valid date.');
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const isoYear = date.getUTCFullYear();
  return {
    isoYear,
    week: Math.ceil((+date - Date.UTC(isoYear, 0, 1) + 86400000) / 604800000),
  };
}
export function weekDate(week: WeekWindow['start']) {
  const date = new Date(Date.UTC(week.isoYear, 0, 4));
  date.setUTCFullYear(week.isoYear);
  date.setUTCDate(
    date.getUTCDate() - (date.getUTCDay() || 7) + 1 + (week.week - 1) * 7
  );
  return date;
}
export const weeksIn = (window: WeekWindow) =>
  (+weekDate(window.end) - +weekDate(window.start)) / 604800000 + 1;
export function minimumDeliveryDate(milestones: PlanningMilestone[]) {
  const first = milestones[0];
  if (!first) throw Error('Add at least one milestone.');
  const date = weekDate(first.window.start);
  const weeks = milestones.reduce((sum, milestone) => {
    const duration = weeksIn(milestone.window);
    if (!Number.isSafeInteger(duration) || duration < 1)
      throw Error('Choose a valid planned week range for every milestone.');
    return sum + duration;
  }, 0);
  date.setUTCDate(date.getUTCDate() + 7 * weeks);
  const minimum = date.toISOString().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(minimum))
    throw Error('The milestone schedule exceeds the supported delivery dates.');
  return minimum;
}
export function deliveryWindow(window: WeekWindow, months: number): WeekWindow {
  const end = weekDate(window.start);
  const day = end.getUTCDate();
  end.setUTCDate(1);
  end.setUTCMonth(end.getUTCMonth() + months);
  const last = new Date(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0)
  ).getUTCDate();
  end.setUTCDate(Math.min(day, last));
  return {
    start: window.start,
    end: dateToWeek(end.toISOString().slice(0, 10)),
  };
}
export const weekText = (week: WeekWindow['start']) =>
  String(week.isoYear).padStart(4, '0') +
  '-W' +
  String(week.week).padStart(2, '0');
export function newMilestone(
  key: number,
  window?: WeekWindow
): PlanningMilestone {
  const week = dateToWeek(new Date().toISOString().slice(0, 10));
  return {
    key,
    title: '',
    window: window ?? { start: week, end: week },
    coordinatorFee: '0',
    coordinatorMinutes: 0,
    requirements: [],
  };
}
export function milestoneTotal(milestone: PlanningMilestone) {
  const amount = (value: string) =>
    /^(0|[1-9][0-9]*)$/.test(value) ? BigInt(value) : 0n;
  return milestone.requirements
    .reduce(
      (sum, requirement) => sum + amount(requirement.budget),
      amount(milestone.coordinatorFee)
    )
    .toString();
}
export function plannedMilestoneBudget(
  milestone: PlanningMilestone,
  coordinatorPercent: number | undefined,
  total = milestoneTotal(milestone)
): PlanningMilestone {
  if (
    coordinatorPercent === undefined ||
    !Number.isInteger(coordinatorPercent) ||
    coordinatorPercent < 0 ||
    coordinatorPercent > 100
  )
    throw Error(
      'The coordinator percentage is unavailable. Reload the catalog.'
    );
  if (!/^(0|[1-9][0-9]*)$/.test(total))
    throw Error('Enter a whole, non-negative KVN budget.');
  const minutes = [
    milestone.coordinatorMinutes,
    ...milestone.requirements.map((r) => r.minutes),
  ];
  if (
    minutes.some(
      (value) => !Number.isInteger(value) || value < 0 || value > 0xffffffff
    )
  )
    throw Error(
      'Enter planned hours that resolve to whole, non-negative minutes.'
    );
  const workerMinutes = minutes.slice(1).map(BigInt);
  const workerEffort = workerMinutes.reduce((sum, value) => sum + value, 0n);
  const percent = BigInt(coordinatorPercent);
  const weights = [
    percent * workerEffort,
    ...workerMinutes.map((value) => (100n - percent) * value),
  ];
  const effort = 100n * workerEffort;
  const budget = BigInt(total);
  if (effort === 0n && budget !== 0n)
    throw Error(
      'Enter planned worker hours before assigning a positive budget.'
    );
  const shares = weights.map((weight) =>
    effort === 0n ? 0n : (budget * weight) / effort
  );
  let remaining = budget - shares.reduce((sum, share) => sum + share, 0n);
  // Ties keep proposal order: coordinator first, then staffing slots.
  const order = weights
    .map((weight, index) => ({
      index,
      remainder: effort === 0n ? 0n : (budget * weight) % effort,
    }))
    .sort((a, b) =>
      a.remainder === b.remainder
        ? a.index - b.index
        : a.remainder > b.remainder
          ? -1
          : 1
    );
  for (const { index } of order) {
    if (remaining === 0n) break;
    shares[index] = shares[index]! + 1n;
    remaining--;
  }
  return {
    ...milestone,
    coordinatorFee: shares[0]!.toString(),
    requirements: milestone.requirements.map((r, index) => ({
      ...r,
      budget: shares[index + 1]!.toString(),
    })),
  };
}
export function currentDetails(
  milestones: PlanningMilestone[],
  details: MilestonePresentation[]
) {
  return milestones.map((milestone) => ({
    key: milestone.key,
    description:
      details.find((item) => item.key === milestone.key)?.description ?? '',
    requirements: milestone.requirements.map((requirement) => ({
      key: requirement.key,
      experience:
        details
          .find((item) => item.key === milestone.key)
          ?.requirements.find((item) => item.key === requirement.key)
          ?.experience ?? null,
    })),
  }));
}
